'use client';
import { useEffect, useRef, useState } from 'react';

import type { BannerState } from '~features/retentionBanners/model/types';
import { useSession } from '~hooks/useSession';
import { userApi } from '~services/api/user';
import { retentionApi } from '~shared/api/retention';
import type { RetentionBannerPlacement } from '~shared/api/retention/types';

export const useGetRetentionBanner = (placement: RetentionBannerPlacement) => {
	const { userId, email } = useSession();

	const requestKeyRef = useRef<string | null>(null);
	const [bannerStatus, setBannerStatus] = useState<BannerState>({
		status: 'idle',
	});
	const { data: userConfigData } = userApi.useUserConfigQuery(
		{ userId },
		{ skip: !userId }
	);

	const [getBanners, { data, error, isLoading }] =
		retentionApi.useBannersConfigMutation({
			fixedCacheKey: `getBanner${placement}`,
		});

	useEffect(() => {
		if (!userConfigData?.retentionJwt || !userId) {
			return;
		}

		const key = `${userId}_${placement}_${userConfigData.retentionJwt}`;
		if (requestKeyRef.current === key) {
			return;
		}

		requestKeyRef.current = key;
		setBannerStatus((prev) =>
			prev.status === 'idle' ? { status: 'loading' } : prev
		);
		void getBanners({
			userId,
			email,
			jwt: userConfigData.retentionJwt,
			placement,
		});
	}, [userConfigData?.retentionJwt, userId, placement, email, getBanners]);

	useEffect(() => {
		if (data?.status === 'inprogress') {
			setBannerStatus({ status: 'generating' });
		} else if (
			data?.status === 'ready' &&
			data?.result.banners.length > 0 &&
			data?.result.placement === placement
		) {
			setBannerStatus({
				status: 'ready',
				banners: data.result.banners.map((banner) => ({
					content: banner.content,
					id: banner.id,
				})),
			});
		} else if (error && (error as { status: number }).status === 404) {
			setBannerStatus({ status: 'no-banner-available' });
		} else if (error) {
			setBannerStatus({ status: 'error' });
		} else if (isLoading) {
			setBannerStatus((prev) =>
				prev.status === 'loading' ? prev : { status: 'loading' }
			);
		}
	}, [data, isLoading, error, placement]);

	return bannerStatus;
};
